import React, { useState, useEffect, useMemo } from "react";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ZAxis } from "recharts";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps } from "./types";
import { WidgetCard } from "../../../components/analytics";

type SortDirection = "asc" | "desc";
type EfficiencySortField = "experiment_title" | "avg_execution_time" | "min_execution_time" | "max_execution_time" | "avg_items_returned";

// Type workarounds for recharts components
const RESP: any = ResponsiveContainer as any;
const SC: any = ScatterChart as any;
const ST: any = Scatter as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const ZA: any = ZAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

/* eslint-disable camelcase */
type EfficiencyDataPoint = {
    experiment_title: string;
    avg_execution_time: number;
    avg_items_returned: number;
    min_execution_time: number;
    max_execution_time: number;
    invocation_count?: number;
};
/* eslint-enable camelcase */

// Color palette for different experiments
const COLORS = [
    "#8884d8", "#82ca9d", "#ffc658", "#ff7c7c", "#8b5cf6",
    "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#6366f1"
];

/**
 * Custom tooltip for scatter chart
 */
const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div style={{ 
                backgroundColor: "white", 
                padding: "10px", 
                border: "1px solid #ccc",
                borderRadius: "4px"
            }}>
                <p style={{ margin: 0, fontWeight: "bold" }}>{data.experiment_title}</p>
                <p style={{ margin: "5px 0 0 0" }}>
                    Avg Execution Time: {data.avg_execution_time.toFixed(2)}ms
                </p>
                <p style={{ margin: "5px 0 0 0" }}>
                    Avg Items Returned: {data.avg_items_returned.toFixed(1)}
                </p>
                <p style={{ margin: "5px 0 0 0" }}>
                    Min/Max Time: {data.min_execution_time.toFixed(2)}ms / {data.max_execution_time.toFixed(2)}ms
                </p>
                {data.invocation_count && (
                    <p style={{ margin: "5px 0 0 0" }}>
                        Invocations: {data.invocation_count.toLocaleString()}
                    </p>
                )}
            </div>
        );
    }
    return null;
};

/**
 * Recommendation Efficiency Widget - Execution time vs items returned by experiment
 */
const RecommendationEfficiencyWidget: React.FC<AnalyticWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    chartOptions: _chartOptions,
    onMetaLoaded
}) => {
    const [analyticsData, setAnalyticsData] = useState<EfficiencyDataPoint[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [widgetMeta, setWidgetMeta] = useState<any>(null);
    const [sortField, setSortField] = useState<EfficiencySortField>("avg_execution_time");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    const sortedData = useMemo(() => {
        return [...analyticsData].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (typeof aVal === "string" && typeof bVal === "string") {
                return sortDirection === "asc" 
                    ? aVal.localeCompare(bVal) 
                    : bVal.localeCompare(aVal);
            }
            return sortDirection === "asc" 
                ? (aVal as number) - (bVal as number) 
                : (bVal as number) - (aVal as number);
        });
    }, [analyticsData, sortField, sortDirection]);

    const handleSort = (field: EfficiencySortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const SortIcon = ({ field }: { field: EfficiencySortField }) => (
        <span style={{ marginLeft: "5px", opacity: sortField === field ? 1 : 0.3 }}>
            {sortField === field && sortDirection === "asc" ? "▲" : "▼"}
        </span>
    );

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);
            
            try {
                const response = await getAnalyticsWidget(
                    customerConfig,
                    "recommendation_efficiency",
                    filters.startDate,
                    filters.endDate
                );
                
                const data = (response as any)?.result;
                const dataArray = Array.isArray(data) ? data : [];
                setAnalyticsData(dataArray);
                const meta = (response as any)?.widget;
                if (meta) {
                    setWidgetMeta(meta);
                    if (onMetaLoaded) {
                        onMetaLoaded({
                            widgetKey: "recommendation_efficiency",
                            displayName: meta.display_name,
                            description: meta.description,
                            schema: (response as any)?.schema
                        });
                    }
                }
            } catch (err) {
                Logger.error("Error fetching recommendation efficiency data", err);
                setError("Failed to fetch recommendation efficiency data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerConfig, filters.startDate, filters.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Group data by experiment for different colored scatter points
    const groupedByExperiment: Record<string, EfficiencyDataPoint[]> = {};
    analyticsData.forEach(point => {
        const key = point.experiment_title || "Unknown";
        if (!groupedByExperiment[key]) {
            groupedByExperiment[key] = [];
        }
        groupedByExperiment[key].push(point);
    });

    const experimentNames = Object.keys(groupedByExperiment);

    return (
        <WidgetCard
            title={widgetMeta?.display_name || "Recommendation Efficiency"}
            description={widgetMeta?.description}
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && !error && analyticsData.length === 0}
            theme={theme}
        >
                    <p style={{ color: "#666", marginBottom: "15px" }}>
                        Plots average execution time against average items returned per experiment.
                        Bubble size indicates invocation count (if available).
                    </p>
                    
                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "20px", 
                        borderRadius: "5px",
                        marginBottom: "20px"
                    }}>
                        <RESP width="100%" height={400}>
                            <SC margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                <CG strokeDasharray="3 3" />
                                <XA 
                                    type="number" 
                                    dataKey="avg_execution_time" 
                                    name="Avg Execution Time (ms)"
                                    unit="ms"
                                />
                                <YA 
                                    type="number" 
                                    dataKey="avg_items_returned" 
                                    name="Avg Items Returned"
                                />
                                <ZA 
                                    type="number" 
                                    dataKey="invocation_count" 
                                    range={[50, 400]} 
                                    name="Invocations"
                                />
                                <TT content={<CustomTooltip />} />
                                <LG />
                                {experimentNames.map((name, index) => (
                                    <ST
                                        key={name}
                                        name={name}
                                        data={groupedByExperiment[name]}
                                        fill={COLORS[index % COLORS.length]}
                                    />
                                ))}
                            </SC>
                        </RESP>
                    </div>

                    {/* Summary table */}
                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "15px", 
                        borderRadius: "5px",
                        marginBottom: "20px",
                        overflowX: "auto"
                    }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "2px solid #ddd" }}>
                                    <th 
                                        style={{ padding: "10px", textAlign: "left", cursor: "pointer" }}
                                        onClick={() => handleSort("experiment_title")}
                                    >
                                        Experiment<SortIcon field="experiment_title" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("avg_execution_time")}
                                    >
                                        Avg Exec Time (ms)<SortIcon field="avg_execution_time" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("min_execution_time")}
                                    >
                                        Min Exec Time<SortIcon field="min_execution_time" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("max_execution_time")}
                                    >
                                        Max Exec Time<SortIcon field="max_execution_time" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("avg_items_returned")}
                                    >
                                        Avg Items Returned<SortIcon field="avg_items_returned" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((row, idx) => (
                                    <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "10px" }}>{row.experiment_title}</td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.avg_execution_time.toFixed(2)}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.min_execution_time.toFixed(2)}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.max_execution_time.toFixed(2)}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.avg_items_returned.toFixed(1)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <details>
                        <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "10px" }}>
                            View Raw Data
                        </summary>
                        <pre style={{ 
                            backgroundColor: "#f5f5f5", 
                            padding: "15px", 
                            borderRadius: "5px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify(analyticsData, null, 2)}
                        </pre>
                    </details>
        </WidgetCard>
    );
};

export default RecommendationEfficiencyWidget;
